<template>
  <div class="flex flex-col gap-6">
    <TextHeader>Energierekening</TextHeader>

    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <ToolbarComponent
          with-portfolio
          no-assets
          no-datepicker
          no-mode
          class="w-fit!"
        />
        <PeriodNavigator
          :label="monthLabel"
          :can-go-prev="canGoPrev"
          :can-go-next="canGoNext"
          :small="!mdAndLarger"
          prev-label="Vorige maand"
          next-label="Volgende maand"
          @prev="prevMonth"
          @next="nextMonth"
        />
      </div>
      <EnergyBillExplainerComponent />
    </div>

    <BaseCard
      v-if="!filterStore.currentMeteringPoint"
      class="p-6 text-center text-gray-400 italic"
      role="status"
    >
      Maak een selectie om gegevens te kunnen bekijken.
    </BaseCard>

    <template v-else>
      <BaseCard
        v-if="loadingFailed"
        class="flex flex-col items-center gap-3 p-6 text-center"
        role="alert"
      >
        <BaseIcon
          name="close-circle"
          filled
          class="text-3xl text-gray-400"
          aria-hidden="true"
        />
        <p class="text-sm text-gray-600">
          De resultaten konden niet worden opgehaald. Probeer het later opnieuw.
        </p>
        <button
          type="button"
          class="bg-primary hover:bg-primary-600 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
          @click="reload"
        >
          Opnieuw proberen
        </button>
      </BaseCard>

      <LoadingWrapper v-else :loading>
        <div class="flex flex-col gap-4">
          <BaseCard
            v-if="hasLoaded && !monthRecord"
            class="flex items-start gap-2 p-4 text-sm text-gray-600"
            role="status"
          >
            <BaseIcon
              name="information"
              filled
              class="mt-0.5 shrink-0 text-base text-gray-400"
              aria-hidden="true"
            />
            <span>
              Voor {{ monthLabel }} zijn nog geen gegevens beschikbaar. Kies een
              andere maand met de navigatieknoppen hierboven.
            </span>
          </BaseCard>

          <EnergyBillKpisPanel
            :month-label="monthLabel"
            :month-record="monthRecord"
            :invoice-total="invoiceTotal"
            :is-definitive="isDefinitive"
            :market-total="marketTotal"
            :is-market-definitive="isMarketDefinitive"
          />

          <MessageComponent v-if="monthRecord && !isDefinitive" color="finance">
            <p>
              <span class="font-medium">Voorlopige resultaten.</span>
              De allocatiedata voor deze maand is nog niet volledig compleet. De
              getoonde bedragen kunnen nog wijzigen totdat de factuur is
              verstuurd.
            </p>
          </MessageComponent>

          <BaseCard class="divide-y divide-gray-100 overflow-hidden p-0">
            <div
              class="flex flex-wrap items-end justify-between gap-3 p-4 md:p-6"
            >
              <div>
                <TextTitle>Onderliggende gegevens</TextTitle>
                <p class="mt-0.5 text-sm text-gray-500">
                  Bekijk de marktresultaten en jouw energierekening per dag of
                  per kwartier.
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-3">
                <RadioButton
                  :items="GRANULARITY_OPTIONS"
                  :initial-value="granularity"
                  @selection-changed="granularity = $event"
                />
                <PeriodNavigator
                  v-if="granularity === GRANULARITY.QUARTER"
                  :label="dayLabel"
                  :can-go-prev="canGoPrevDay"
                  :can-go-next="canGoNextDay"
                  prev-label="Vorige dag"
                  next-label="Volgende dag"
                  label-class="min-w-32"
                  @prev="prevDay"
                  @next="nextDay"
                />
              </div>
            </div>

            <MarketResultsChart
              :granularity
              :chart-type
              :chart-series
              :chart-axes
              :chart-data
              :chart-limits
              :chart-time-unit
              :loading-quarter
              :quarter-failed
              @retry="reloadQuarter"
            />

            <InvoiceLinesCard
              :groups="groupedInvoiceLines"
              :is-definitive
              :granularity
              :loading-quarter
              :quarter-failed
              :default-row-limit="DEFAULT_DETAIL_ROW_LIMIT"
              :invoice-total
            />
          </BaseCard>

          <BaseCard class="p-4 md:p-6">
            <div class="flex items-center justify-between">
              <div>
                <TextLabel class="font-medium text-gray-700">
                  Totaal factuurbedrag
                </TextLabel>
                <BaseBadge
                  v-if="monthRecord"
                  :class="
                    isDefinitive
                      ? 'bg-success/10 text-success text-xs'
                      : 'bg-warning/10 text-warning text-xs'
                  "
                >
                  <BaseIcon
                    :name="isDefinitive ? 'checkbox-circle' : 'time'"
                    filled
                    class="mr-1 text-xs"
                    aria-hidden="true"
                  />
                  {{
                    isDefinitive
                      ? "Definitieve resultaten"
                      : "Voorlopige resultaten"
                  }}
                </BaseBadge>
              </div>
              <div class="text-2xl font-bold text-gray-900">
                {{ formatEuro(invoiceTotal) }}
              </div>
            </div>
          </BaseCard>
        </div>
      </LoadingWrapper>
    </template>

    <div
      v-if="filterStore.currentMeteringPoint && !loadingFailed"
      class="flex justify-end"
    >
      <ExportComponent
        :metering-point="filterStore.currentMeteringPoint"
        :date-range="monthDateRange"
        :type="EXPORT_TYPES.RECORDS"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core"
import { format } from "date-fns"
import { nl } from "date-fns/locale"
import { formatEuro } from "@/helpers/energyBillFormat.js"
import usePortfolioAsDefaultMeteringPoint from "@/composables/usePortfolioAsDefaultMeteringPoint.js"
import useEnergyBillData from "@/composables/energyBill/useEnergyBillData.js"
import useMonthNavigation from "@/composables/energyBill/useMonthNavigation.js"
import useDayNavigation from "@/composables/energyBill/useDayNavigation.js"
import useInvoiceLines from "@/composables/energyBill/useInvoiceLines.js"
import useMarketChart from "@/composables/energyBill/useMarketChart.js"
import {
  DEFAULT_DETAIL_ROW_LIMIT,
  GRANULARITY,
  GRANULARITY_OPTIONS,
  LEVELS,
} from "@/helpers/energyBillConstants.js"
import { ASSET } from "@/stores/repower/meteringPointStoreFactories.js"
import { EXPORT_TYPES } from "@/stores/repower/exportsStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  BaseBadge,
  BaseCard,
  BaseIcon,
  LoadingWrapper,
  RadioButton,
  TextHeader,
  TextLabel,
  TextTitle,
} from "@repowerednl/ui-component-library"
import ExportComponent from "@/components/information/ExportComponent.vue"
import ToolbarComponent from "@/components/navigation/ToolbarComponent.vue"
import PeriodNavigator from "@/components/panels/energyBill/PeriodNavigator.vue"
import EnergyBillExplainerComponent from "@/components/panels/energyBill/EnergyBillExplainerComponent.vue"
import EnergyBillKpisPanel from "@/components/panels/energyBill/EnergyBillKpisPanel.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"
import MarketResultsChart from "@/components/panels/energyBill/MarketResultsChart.vue"
import InvoiceLinesCard from "@/components/panels/energyBill/InvoiceLinesCard.vue"

const filterStore = useFilterStore()
usePortfolioAsDefaultMeteringPoint([ASSET])

const mdAndLarger = useBreakpoints(breakpointsTailwind).greaterOrEqual("md")

// --- Level (portfolio vs allocation point) ---
// The metering-point picker drives the selection; the level is derived from it
// and consumed by useInvoiceLines.
const level = computed(() => {
  const mp = filterStore.currentMeteringPoint
  return !mp || mp.id === filterStore.currentPortfolio?.id
    ? LEVELS.PORTFOLIO
    : LEVELS.ALLOCATION_POINT
})

// --- Granularity ---
const granularity = ref(GRANULARITY.DAY)

// --- Month navigation ---
const {
  selectedMonth,
  monthLabel,
  monthDateRange,
  canGoPrev,
  canGoNext,
  prevMonth,
  nextMonth,
} = useMonthNavigation()

// --- Data ---
const {
  monthlyData,
  dailyData,
  quarterData,
  loading,
  loadingQuarter,
  loadingFailed,
  quarterFailed,
  reload,
  reloadQuarter,
} = useEnergyBillData(
  () => filterStore.currentMeteringPoint,
  () => monthDateRange.value,
  () => granularity.value,
)

const hasLoaded = computed(
  () => !loading.value && !loadingFailed.value && monthlyData.value !== null,
)

const monthRecord = computed(() => {
  const records = monthlyData.value?.records
  if (!records) {
    return null
  }
  // The MS-frequency request is scoped to a single month, so we expect at
  // most one record; pick it directly instead of scanning all keys.
  const entries = Object.values(records)
  return entries.length ? entries[0].data : null
})

const dailyRecords = computed(() => {
  const records = dailyData.value?.records
  if (!records) {
    return []
  }
  return Object.entries(records)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([datetime, record]) => {
      const d = new Date(datetime)
      return {
        datum: format(d, "d MMM yyyy", { locale: nl }),
        dayKey: format(d, "yyyy-MM-dd"),
        ...record.data,
      }
    })
})

const quarterRecords = computed(() => {
  const records = quarterData.value?.records
  if (!records) {
    return []
  }
  return Object.entries(records)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([datetime, record]) => {
      const d = new Date(datetime)
      return {
        datum: format(d, "HH:mm"),
        dayKey: format(d, "yyyy-MM-dd"),
        ...record.data,
      }
    })
})

// --- Day navigation (within the chart's quarter view) ---
const availableDays = computed(() => {
  const set = new Set(dailyRecords.value.map((r) => r.dayKey))
  return Array.from(set).sort()
})

const {
  effectiveDayKey,
  dayLabel,
  canGoPrevDay,
  canGoNextDay,
  prevDay,
  nextDay,
} = useDayNavigation(availableDays, selectedMonth)

const selectedDayQuarterRecords = computed(() => {
  const key = effectiveDayKey.value
  if (!key) {
    return []
  }
  return quarterRecords.value.filter((r) => r.dayKey === key)
})

// --- Totals ---
const isDefinitive = computed(
  () => monthRecord.value?.TOTAL_RESULT?.stable === true,
)
const invoiceTotal = computed(
  () => monthRecord.value?.TOTAL_RESULT?.value ?? null,
)

// --- Invoice lines ---
const { groupedInvoiceLines } = useInvoiceLines({
  monthRecord,
  dailyRecords,
  monthLabel,
  level,
  quarterRecords: selectedDayQuarterRecords,
})

// --- Market chart ---
const {
  chartSeries,
  chartAxes,
  chartData,
  chartLimits,
  chartTimeUnit,
  chartType,
  marketTotal,
  isMarketDefinitive,
} = useMarketChart({
  monthRecord,
  selectedMonth,
  granularity,
  dailyData,
  quarterData,
  effectiveDayKey,
})
</script>
